import { Box, CircularProgress, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import MosInfoProvider from '../Widgets/common/provider/MosInfoProvider'
import WidgetGrid from '../Widgets/base/WidgetGrid'
import { parseTemplateWidgets } from './widgetTemplateParse'

const HEADER_HEIGHT = '3.5rem'

/**
 * Full-screen editor opened from the Assets "Change widget template" list via
 * "Edit and apply": loads the chosen template's layout into the MOS grid, lets
 * the user modify it, and saves the result to the SELECTED DEVICE's layout
 * (POST widgets/device/{id}) — the template itself is left unchanged.
 */
const EditAndApplyTemplateDialog = ({ open, template, device, onClose }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [widgets, setWidgets] = useState([])
  const [editing, setEditing] = useState(true)
  const [loading, setLoading] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    if (!open || !template?.id) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const full = await apiGet('widgets/templates/' + template.id)
        if (!cancelled) setWidgets(parseTemplateWidgets(full?.widgets))
      } catch (e) {
        if (!cancelled) showAlert({ title: t_('error loading widget template'), message: t_(e.message || 'Unknown error'), severity: 'error' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open, template, reloadToken, showAlert, t_])

  const reloadWidgets = useCallback(() => setReloadToken((token) => token + 1), [])

  const onSave = useCallback(async () => {
    if (!device) return
    try {
      await apiPost('widgets/device/' + device.id, widgets.map((item) => ({ ...item, data: item.data ? JSON.stringify(item.data) : null })))
      showAlert({ title: t_('Template applied'), message: '', severity: 'success' })
      onClose(true)
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    }
  }, [device, widgets, showAlert, t_, onClose])

  return (
    <Dialog fullScreen open={open} onClose={() => onClose(false)} PaperProps={{ sx: { overflow: 'hidden' } }}>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ px: 2, height: HEADER_HEIGHT, flexShrink: 0, boxSizing: 'border-box', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title={t_('Back')}>
          <IconButton onClick={() => onClose(false)} aria-label={t_('Back')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant='h6' sx={{ whiteSpace: 'nowrap' }}>{t_('Edit and apply')}</Typography>
        <Typography color='text.secondary' noWrap>
          {template?.name}{device ? ' → ' + device.name : ''}
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ flexGrow: 1, textAlign: 'right' }}>
          {t_('Save applies the changes to this device only; the template is not modified')}
        </Typography>
      </Stack>
      {/* Scroll container with a definite height; the inner flex item grows with
          the grid content so the WidgetGrid's height:100% resolves AND the sticky
          toolbar's containing block spans the whole content (stays pinned on scroll). */}
      <Box sx={{ height: 'calc(100vh - ' + HEADER_HEIGHT + ')', overflow: 'auto' }}>
        <Stack direction='column' sx={{ height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <div style={{ position: 'sticky', top: '0.5rem', zIndex: 100, padding: '0 1rem' }}>{AlertElement}</div>
            {loading
              ? <Stack justifyContent='center' alignItems='center' sx={{ height: '100%' }}><CircularProgress size='6rem' /></Stack>
              : (
                <MosInfoProvider>
                  <WidgetGrid
                    widgets={widgets}
                    setWidgets={setWidgets}
                    reloadWidgets={reloadWidgets}
                    onSave={onSave}
                    editing={editing}
                    setEditing={setEditing}
                    showAllWidgets
                    toolbarLabel={device?.name}
                  />
                </MosInfoProvider>
                )}
          </Box>
        </Stack>
      </Box>
    </Dialog>
  )
}

export default EditAndApplyTemplateDialog
